import React, { useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Link,
} from '@chakra-ui/react';

const FAQ = () => {
  const headingColor = 'gray.900';
  const textColor = 'gray.600';
  const linkColor = '#135200';

  // Scroll to the targeted question when the page is opened with a #hash link.
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    // Defer so it runs after ScrollToTop and after content has laid out.
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box as="main" minH="100vh" bg="gray.50" pt={20} pb={20}>
      <Container maxW="container.lg">
        <VStack spacing={10} align="start">
          {/* Page Header */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              size="2xl"
              color={headingColor}
              fontWeight="normal"
              lineHeight="1.15"
              letterSpacing="-0.01em"
            >
              Frequently Asked Questions
            </Heading>
            <Text fontSize="lg" color={textColor} lineHeight="relaxed">
              Please reach us at{' '}
              <Link
                href="mailto:contact@email-cleaner.app"
                color={linkColor}
                textDecoration="underline"
                _hover={{ color: '#389e0d' }}
              >
                contact@email-cleaner.app
              </Link>
              {' '}if you cannot find an answer to your question
            </Text>
          </VStack>

          {/* FAQ Item 1 */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              id="what-is-clean-gmail"
              size="lg"
              color={headingColor}
              fontWeight="normal"
              scrollMarginTop="100px"
            >
              What is Email Cleaner?
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Email Cleaner is a lightweight Chrome extension that works directly with your Gmail account
              to help you take back your inbox. It analyzes your inbox, detects senders, and identifies
              patterns -- who emails you most, what you never open, what is just noise. Based on that,
              it suggests cleanup rules tailored to your inbox. You review and approve every rule before
              it takes effect. Once activated, matching emails are automatically removed as they arrive,
              keeping your inbox clean without you lifting a finger.
            </Text>
          </VStack>

          {/* FAQ Item 2 */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              id="does-clean-gmail-read-my-emails"
              size="lg"
              color={headingColor}
              fontWeight="normal"
              scrollMarginTop="100px"
            >
              Does Email Cleaner read my emails?
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Email Cleaner runs entirely in your browser. There is no server collecting your emails and
              no cloud storage of your messages. The extension only looks at sender information and
              email metadata to suggest cleanup rules. Your Gmail credentials are handled through
              Google's official OAuth system -- the extension never sees or stores your password.
              We use Firebase Analytics to understand how the extension is used and improve the
              experience, but your email content always stays between you, your browser, and Google.
            </Text>
          </VStack>

          {/* FAQ Item 3 */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              id="undo-rule-or-restore-deleted-emails"
              size="lg"
              color={headingColor}
              fontWeight="normal"
              scrollMarginTop="100px"
            >
              Can I undo a rule or restore deleted emails?
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              Active rules can be paused at any time if you want to temporarily stop them, or
              disabled if you no longer need them. Disabled rules are not gone forever — you can
              always restore them later. If you edit an active rule, it automatically moves back
              to a pending state so you can confirm the changes before they take effect.
              Emails that were archived or marked as read can be reverted at any time as long as
              they weren't deleted. If an email was deleted, you can restore it from Gmail's Trash
              within 30 days.
            </Text>
          </VStack>

          {/* FAQ Item 4 */}
          <VStack spacing={4} align="start" w="full">
            <Heading
              id="request-deletion-of-my-data"
              size="lg"
              color={headingColor}
              fontWeight="normal"
              scrollMarginTop="100px"
            >
              How can I request the deletion of my data?
            </Heading>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              At Email Cleaner, we only collect anonymous analytics related to extension usage and your email address through OAuth.
            </Text>
            <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
              If you wish to have your information deleted, please follow these steps:
            </Text>
            <VStack spacing={3} align="start" w="full" pl={4}>
              <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
                <Text as="span" fontWeight="semibold">Email Us:</Text> Send an email to{' '}
                <Link
                  href="mailto:contact@email-cleaner.app"
                  color={linkColor}
                  textDecoration="underline"
                  _hover={{ color: '#389e0d' }}
                >
                  contact@email-cleaner.app
                </Link>
                {' '}with a request to delete your data.
              </Text>
              <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
                <Text as="span" fontWeight="semibold">Confirmation:</Text> We will confirm receipt of your request and process it as quickly as possible.
              </Text>
              <Text fontSize="md" color={textColor} lineHeight="relaxed" maxW="65ch">
                <Text as="span" fontWeight="semibold">Deletion Process:</Text> Once your request has been processed,
                all your associated data, including your email address, will be permanently deleted from our records.
                Anonymous analytics data that does not personally identify you may still be retained for statistical purposes.
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default FAQ;
